import React, { forwardRef, useRef } from "react";
import type {
    JSX,
    PropsWithChildren,
    RefObject,
    FC,
    CSSProperties,
} from "react";

import cn from "classnames";

import useDropdownPosition from "../../../hooks/useDropdownPosition";
import { Portal } from "../Portal/Portal";
import { Transition } from "../Transition/Transition";

import type {
    DropdownAlignment,
    DropdownSide,
} from "../../../hooks/useDropdownPosition";
import type {
    Never,
    PropsWithCustomVarsStyles,
    PropsWithTsId,
} from "../../../types/react";
import type { IPortal } from "../Portal/Portal";

import classes from "./Popup.styl";

type PropsWithPortal<T = unknown> = T &
    (
        | (IPortal & {
              /**
               * Отвечает за позиционировние всплывашки в вёрстке. Если false, то меню будет отрендерено не в основном
               * контейнере, а через портал в элементе, который указывается в коллбеке getContainer.
               */
              inline: false;
          })
        | (Never<IPortal> & {
              /**
               * Если true, то меню будет отрендерено в основном контейнере.
               */
              inline?: true;
          })
    );

export type IPopup = PropsWithCustomVarsStyles<
    PropsWithPortal<{
        /** Элемент, относительно которого будем производить позиционирование */
        targetRef: RefObject<HTMLElement>;
        /** Сторона, с которой показываем выпадашку */
        side?: DropdownSide;
        /** Расположение относительно таргета */
        alignment?: DropdownAlignment;
        /** Отвечает за отключение перемещения (блокировку) компонента в видимую часть экрана при частичном скрытии */
        adjustmentLocked?: boolean;
        /** Если у нас происходит наложение попапов друг на друга, с помощью этого параметра сможем их правильно спозиционировать */
        zIndex?: number;
    }>
>;

type PopupContentProps = PropsWithTsId<PropsWithChildren<IPopup>>;

/**
 * Главный компонент, который отвечает за позиционирование компонента-контейнера относительно компонента-таргета
 */
const PopupContent = forwardRef<HTMLDivElement, PopupContentProps>(
    (
        {
            portalClassName,
            getContainer,
            customVarsStyles,
            inline = true,
            children,
            targetRef,
            adjustmentLocked = true,
            side = "bottom",
            alignment = "start",
            "data-tsid": tsid,
            zIndex = 0,
            ...props
        },
        ref,
    ): JSX.Element | null => {
        const containerRef = useRef<HTMLDivElement>(null);

        const { dropdownSide, dropdownAlignment, position } =
            useDropdownPosition({
                targetRef,
                containerRef,
                side,
                alignment,
                adjustmentLocked,
            });

        const wrapperClasses = cn(
            classes["wrapper"],
            classes[`__alignment-${dropdownAlignment}`],
            classes[`__side-${dropdownSide}`],
            customVarsStyles,
        );

        if (inline) {
            const customWrapperClasses = cn(
                wrapperClasses,
                classes["__inline"],
            );

            return (
                <div ref={ref}>
                    <div
                        className={customWrapperClasses}
                        data-tsid={tsid}
                        ref={containerRef}
                    >
                        {children}
                    </div>
                </div>
            );
        }

        const styles = {
            ...position,
            "--popup-z-index": zIndex,
        } as CSSProperties;

        return (
            <Portal
                {...props}
                portalClassName={portalClassName!}
                getContainer={getContainer!}
                className={wrapperClasses}
                style={styles}
                data-tsid={tsid}
                containerRef={containerRef}
                ref={ref}
            >
                {children}
            </Portal>
        );
    },
);

PopupContent.displayName = "Popup.Content";

const ANIMATION_DELAY = 400;

type PopupProps = PopupContentProps & {
    isOpen: boolean;
};

export const Popup: FC<PopupProps> = ({
    isOpen,
    adjustmentLocked = true,
    ...props
}) => {
    // inline в props имеет тип true | false из-за юниона, поэтому мы должны сохранить этот тип для передачи далее
    const inline = props.inline ?? true;

    const transitionWrapperRef = useRef<HTMLDivElement>(null);

    /** Создаём сложный ключ, чтобы инициировать ререндер анимации при изменении пропсов */
    const hardKey = `${inline}-${props.side}-${props.alignment}-${adjustmentLocked}`;

    return (
        // eslint-disable-next-line ok-custom-rules/check-attribute
        <Transition
            key={hardKey}
            isShow={isOpen}
            timeout={ANIMATION_DELAY}
            classes={{
                enterActive: classes["enter-active"],
                enterDone: classes["enter-done"],
                exitActive: classes["exit-active"],
                exitDone: classes["exit-done"],
            }}
            skipFirstRender
            alwaysInDOM={!adjustmentLocked && !inline}
            refElement={transitionWrapperRef}
        >
            <PopupContent
                {...props}
                adjustmentLocked={adjustmentLocked}
                ref={transitionWrapperRef}
            />
        </Transition>
    );
};

Popup.displayName = "Popup";
